import {FC, useRef, useEffect, useState} from 'react'
import {useIntl} from 'react-intl'
import {RouteComponentProps, useLocation} from 'react-router-dom'
import './OrderDetail.refactored.css'
import {PageTitle} from '../../../_metronic/layout/core'
import MasonryGallery from './components/MasonryGallery'
import ListGallery from './components/ListGallery'
import OrderDetailHeader from './components/OrderDetailHeader'
import {LayoutMode} from './components/OrderDetailHeader'
import {useDispatch} from 'react-redux'
import {
  filterOrderItemsChange,
  getAiServiceStatus,
  getDetail,
  selectAiServiceStatus,
  selectCurrentOrder,
  selectCurrentOrderItems,
  selectCurrentOrderItemsFilter,
  setAsyncWorkingOrders,
  setSelectMode,
} from './redux/OrderRedux'
import {useAppSelector} from '../../../setup/redux/hooks'
import {OrderActionType, ItemStatus} from './constants'
import InfiniteScroll from 'react-infinite-scroll-component'
import {toast, ToastPosition} from 'react-toastify'
import {OrderActionModel} from './models/OrderActionModel'
import {getIntl} from '../../shared/intl'
import {useOrderRealtime} from './hooks/useOrderRealtime'
import {useAiProcessing} from './hooks/useAiProcessing'

interface RouteParams {
  id: string
}
interface Props extends RouteComponentProps<RouteParams> {}

const OrderDetail: FC<Props> = (props) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const didRequest = useRef(false)

  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const downloadUrl = queryParams.get('downloadUrl')
  const orderId = props.match.params.id

  const [layoutMode, setLayoutMode] = useState<LayoutMode>(LayoutMode.List)

  const isAiServerOverload = useAppSelector(selectAiServiceStatus)
  const order = useAppSelector(selectCurrentOrder)
  const orderItems = useAppSelector(selectCurrentOrderItems)
  const orderItemsFilter = useAppSelector(selectCurrentOrderItemsFilter)

  // Custom hooks
  useOrderRealtime(orderId)
  const {isProcessing, progress, messageId} = useAiProcessing(order)

  // Sort completed items to the top during AI processing
  const sortedItems = isProcessing
    ? [...orderItems].sort((a, b) => {
        const aDone = a.status === ItemStatus.DONE ? 0 : 1
        const bDone = b.status === ItemStatus.DONE ? 0 : 1
        return aDone - bDone
      })
    : orderItems

  // Initial data fetch
  useEffect(() => {
    if (!didRequest.current) {
      dispatch(getAiServiceStatus())
      dispatch(getDetail(orderId))

      if (downloadUrl) {
        const intlInstance = getIntl()
        const downloadToast = toast.info(
          intlInstance.formatMessage({id: 'MESSAGE.ORDER.DOWNLOADING'}),
          {
            hideProgressBar: true,
            position: 'bottom-right' as ToastPosition,
            autoClose: false,
          }
        )
        dispatch(
          setAsyncWorkingOrders({
            orderId: Number(orderId),
            type: OrderActionType.Download,
            asyncListenerPath: downloadUrl,
            toast: downloadToast,
          } as OrderActionModel)
        )
      }
    }
    return () => {
      didRequest.current = true
      dispatch(setSelectMode(false))
    }
  }, [downloadUrl, orderId, dispatch])

  const fetchMore = () => {
    dispatch(
      filterOrderItemsChange({...orderItemsFilter, page: Number(orderItemsFilter.page) + 1})
    )
  }

  return (
    <>
      {/* Breadcrumb */}
      {order && (
        <PageTitle
          breadcrumbs={[
            {
              title: intl.formatMessage({id: 'ORDER.LIST'}),
              path: '/orders',
              isSeparator: false,
              isActive: true,
            },
            {
              title: 'dash',
              path: 'dash',
              isSeparator: true,
              isActive: true,
            },
          ]}
        >
          {order.name}
        </PageTitle>
      )}

      {order && (
        <>
          {/* Overload warning */}
          {isProcessing && isAiServerOverload && (
            <div className='order-overload-warning'>
              <div className='d-flex bg-light-warning rounded border-warning border border-dashed p-4'>
                <i className='bi bi-exclamation-circle-fill fs-2tx text-warning me-4'></i>
                <div className='fw-bold'>
                  <h4 className='text-gray-800 fw-bolder mb-1'>
                    {intl.formatMessage({id: 'Order.ai.overloaded.title'})}
                  </h4>
                  <div className='fs-6 text-gray-600'>
                    {intl.formatMessage({id: 'Order.ai.overloaded'})}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Header: order info + progress bar + actions */}
          <OrderDetailHeader
            order={order}
            progress={progress}
            messageId={messageId}
            isProcessing={isProcessing}
            layoutMode={layoutMode}
            onLayoutChange={setLayoutMode}
          />

          {/* Gallery */}
          <InfiniteScroll
            dataLength={orderItems.length}
            next={fetchMore}
            style={{overflowX: 'hidden'}}
            hasMore={orderItems.length !== orderItemsFilter.itemCount}
            loader={<div />}
          >
            {layoutMode === LayoutMode.Masonry ? (
              <MasonryGallery items={sortedItems} />
            ) : (
              <ListGallery items={sortedItems} />
            )}
          </InfiniteScroll>
        </>
      )}
    </>
  )
}

export {OrderDetail}
